import { Component } from '@theme/component';

/**
 * Een knop die tussen een korte en een volledige weergave wisselt, met
 * "Toon alle …" / "Toon minder" als label.
 *
 * Wat er verborgen of getoond wordt staat in de CSS van het block, op het
 * `expanded`-attribuut. Zonder JavaScript krijgt het component nooit het
 * `enhanced`-attribuut en blijft de knop verborgen; de CSS toont dan de
 * volledige weergave.
 *
 * @typedef {object} ShowMoreRefs
 * @property {HTMLButtonElement} toggle - De knop; heeft `data-more-label` en `data-less-label`.
 *
 * @template {ShowMoreRefs} [T=ShowMoreRefs]
 * @extends Component<T>
 */
export class ShowMoreComponent extends Component {
  requiredRefs = ['toggle'];

  connectedCallback() {
    super.connectedCallback();

    this.setAttribute('enhanced', '');
    this.refs.toggle.hidden = false;
  }

  toggle() {
    const { toggle } = this.refs;
    const expanded = !this.hasAttribute('expanded');

    this.toggleAttribute('expanded', expanded);
    toggle.setAttribute('aria-expanded', `${expanded}`);
    toggle.textContent = (expanded ? toggle.dataset.lessLabel : toggle.dataset.moreLabel) ?? '';
  }
}

/**
 * Kapt een lange tekst af met een "Lees meer"-knop.
 *
 * Hoe hoog de tekst afgekapt wordt (en op welke breedte) staat in de CSS van
 * het block dat dit component gebruikt; hier wordt alleen gemeten of de tekst
 * daadwerkelijk langer is. Is hij korter, dan blijft de knop weg.
 *
 * @typedef {object} ReadMoreRefs
 * @property {HTMLElement} content - De tekst die afgekapt wordt.
 * @property {HTMLButtonElement} toggle - De knop "Lees meer" / "Lees minder".
 *
 * @extends ShowMoreComponent<ReadMoreRefs>
 */
export class ReadMoreComponent extends ShowMoreComponent {
  requiredRefs = ['content', 'toggle'];

  /** @type {ResizeObserver | undefined} */
  #observer;

  connectedCallback() {
    super.connectedCallback();

    this.#observer = new ResizeObserver(() => this.#measure());
    this.#observer.observe(this.refs.content);
    this.#measure();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#observer?.disconnect();
  }

  /**
   * Ingeklapt is de inhoud lager dan zijn scrollhoogte zodra de tekst niet
   * past; uitgeklapt blijft de knop staan, anders kan de bezoeker niet terug.
   */
  #measure() {
    if (this.hasAttribute('expanded')) return;

    const { content, toggle } = this.refs;
    const overflowing = content.scrollHeight > content.clientHeight + 1;

    this.toggleAttribute('overflowing', overflowing);
    toggle.hidden = !overflowing;
  }
}

if (!customElements.get('show-more-component')) {
  customElements.define('show-more-component', ShowMoreComponent);
}

if (!customElements.get('read-more-component')) {
  customElements.define('read-more-component', ReadMoreComponent);
}
